import React, { useState, useCallback } from 'react';
import { 
  Users, User, UserCircle, Phone, Calendar, CreditCard, Globe, 
  FileText, Eye, Download, Plane, X, Plus, Trash2, Edit2, 
  ChevronRight, AlertTriangle, Copy, ChevronDown, ChevronUp
} from 'lucide-react';
import { getNames, getCodes } from 'country-list';
import { 
  addBusiness4FlyoTraveller, 
  updateBusiness4FlyoTraveller, 
  deleteBusiness4FlyoTraveller
} from '../../services/api';

// Gender options - defined outside component to prevent recreation
const genderOptions = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

// Get nationality options - defined outside component
const getNationalityOptions = () => {
  const names = getNames();
  const codes = getCodes();
  return names.map((name, index) => ({
    value: codes[index],
    label: name
  })).sort((a, b) => a.label.localeCompare(b.label));
};

const nationalityOptions = getNationalityOptions();

// Popular nationalities for quick access
const popularNationalities = ['US', 'GB', 'IN', 'CA', 'AU', 'DE', 'FR', 'IT', 'ES', 'JP', 'CN', 'AE', 'SA', 'SG', 'MY'];

// Get country codes for phone - defined outside component
const countryCodeOptions = [
  { value: '+1', label: '+1 (US/CA)' },
  { value: '+44', label: '+44 (UK)' },
  { value: '+91', label: '+91 (IN)' },
  { value: '+86', label: '+86 (CN)' },
  { value: '+81', label: '+81 (JP)' },
  { value: '+49', label: '+49 (DE)' },
  { value: '+33', label: '+33 (FR)' },
  { value: '+39', label: '+39 (IT)' },
  { value: '+34', label: '+34 (ES)' },
  { value: '+61', label: '+61 (AU)' },
  { value: '+971', label: '+971 (AE)' },
  { value: '+966', label: '+966 (SA)' },
  { value: '+974', label: '+974 (QA)' },
  { value: '+965', label: '+965 (KW)' },
  { value: '+973', label: '+973 (BH)' },
  { value: '+968', label: '+968 (OM)' },
  { value: '+92', label: '+92 (PK)' },
  { value: '+880', label: '+880 (BD)' },
  { value: '+94', label: '+94 (LK)' },
  { value: '+95', label: '+95 (MM)' },
  { value: '+66', label: '+66 (TH)' },
  { value: '+60', label: '+60 (MY)' },
  { value: '+62', label: '+62 (ID)' },
  { value: '+65', label: '+65 (SG)' },
  { value: '+84', label: '+84 (VN)' },
  { value: '+63', label: '+63 (PH)' },
].sort((a, b) => a.label.localeCompare(b.label));

// Color classes - defined outside component
const colorClasses = {
  blue: 'bg-blue-50 text-blue-600',
  purple: 'bg-purple-50 text-purple-600',
  green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
};

/**
 * Traveller Tab Component for Business4Flyo
 * Aesthetic & Futuristic Design - No Gradients
 * @param {Object} props - Component props
 * @param {Array} props.flights - Array of flight objects with travellers
 * @param {number} props.ticketId - Ticket ID from URL
 * @param {number} props.clientId - Client ID from booking data
 * @param {number} props.agentId - Agent ID from booking data
 * @param {Function} props.onUpdate - Callback to refresh data after update
 * @returns {JSX.Element} B4FTravellerTab component
 */
const B4FTravellerTab = ({ flights, ticketId, clientId, agentId, onUpdate }) => {
  const [showTravellerModal, setShowTravellerModal] = useState(false);
  const [editingTraveller, setEditingTraveller] = useState(null);
  const [selectedFlightTab, setSelectedFlightTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [travellerToDelete, setTravellerToDelete] = useState(null);
  
  // Inline edit state
  const [inlineEditingField, setInlineEditingField] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState('');
  
  // Expanded travellers state - all collapsed by default
  const [expandedTravellers, setExpandedTravellers] = useState(new Set());
  
  // Toggle traveller expansion
  const toggleTravellerExpand = useCallback((travellerId) => {
    setExpandedTravellers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(travellerId)) {
        newSet.delete(travellerId);
      } else {
        newSet.add(travellerId);
      }
      return newSet;
    });
  }, []);
  
  // Form data structure with flightData array
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    countryCode: '+1',
    phone: '',
    dateOfBirth: '',
    passport: '',
    passportExpiry: '',
    nationality: '',
    gender: '',
    flightData: []
  });

  // Initialize flightData when flights change or modal opens
  const initializeFlightData = useCallback((isEdit = false, traveller = null) => {
    if (!flights || flights.length === 0) return [];
    
    return flights.map((flight) => {
      if (isEdit && traveller) {
        return {
          flightId: flight.flightId,
          seat: traveller.seat || '',
          meal: traveller.meal || '',
          baggageCabin: traveller.baggage?.cabin || '',
          baggageCheckin: traveller.baggage?.checkin || '',
          baggageAdditional: traveller.baggage?.additional || ''
        };
      }
      
      return {
        flightId: flight.flightId,
        seat: '',
        meal: '',
        baggageCabin: '',
        baggageCheckin: '',
        baggageAdditional: ''
      };
    });
  }, [flights]);

  // Format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  // Format sector string
  const formatSector = (departure, arrival) => {
    const dep = departure?.iata || 'N/A';
    const arr = arrival?.iata || 'N/A';
    return `${dep} → ${arr}`;
  };

  // Get first flight's travellers
  const firstFlight = flights && flights.length > 0 ? flights[0] : null;
  const travellers = firstFlight?.travellers || [];

  // Open modal for adding traveller
  const handleAddTraveller = () => {
    setEditingTraveller(null);
    setSelectedFlightTab(0);
    setFormData({
      firstName: '',
      lastName: '',
      countryCode: '+1',
      phone: '',
      dateOfBirth: '',
      passport: '',
      passportExpiry: '',
      nationality: '',
      gender: '',
      flightData: initializeFlightData(false)
    });
    setShowTravellerModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (traveller) => {
    setTravellerToDelete(traveller);
    setShowDeleteModal(true);
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTravellerToDelete(null);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!travellerToDelete) return;
    
    if (!agentId || !ticketId || !firstFlight) {
      alert('Missing required information');
      return;
    }

    try {
      setLoading(true);
      await deleteBusiness4FlyoTraveller({
        agentId,
        ticketId: parseInt(ticketId),
        flightId: parseInt(firstFlight.flightId),
        travellerId: travellerToDelete.travellerId
      });
      closeDeleteModal();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error deleting traveller: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Start inline editing
  const startInlineEdit = useCallback((traveller, field, value) => {
    setEditingTraveller(traveller);
    setInlineEditingField(field);
    setInlineEditValue(value || '');
  }, []);

  // Cancel inline editing
  const cancelInlineEdit = useCallback(() => {
    setInlineEditingField(null);
    setInlineEditValue('');
    setEditingTraveller(null);
  }, []);

  // Save inline edit
  const saveInlineEdit = useCallback(async () => {
    if (!editingTraveller || !inlineEditingField) return;
    if (!agentId || !ticketId || !clientId) {
      alert('Missing required information');
      return;
    }

    try {
      setLoading(true);

      // Build minimal payload with only required IDs and the field being updated
      const payload = {
        agentId: parseInt(agentId),
        travellerId: editingTraveller.travellerId,
        ticketId: parseInt(ticketId),
        clientId: parseInt(clientId),
        firstName: editingTraveller.firstname || '',
        lastName: editingTraveller.lastname || ''
      };

      // Only include the specific field being edited
      switch (inlineEditingField) {
        case 'phone':
          payload.phone = inlineEditValue;
          break;
        case 'countryCode':
          payload.countryCode = inlineEditValue;
          break;
        case 'passport':
          payload.passport = inlineEditValue;
          break;
        case 'nationality':
          payload.nationality = inlineEditValue;
          break;
        case 'gender':
          payload.gender = inlineEditValue;
          break;
        case 'dateOfBirth':
          // Only send date if it has a value, otherwise don't include it
          if (inlineEditValue) {
            payload.dateOfBirth = inlineEditValue;
          }
          break;
        case 'passportExpiry':
          // Only send date if it has a value, otherwise don't include it
          if (inlineEditValue) {
            payload.passportExpiry = inlineEditValue;
          }
          break;
        case 'firstname':
          payload.firstName = inlineEditValue;
          break;
        case 'lastname':
          payload.lastName = inlineEditValue;
          break;
        default:
          break;
      }

      await updateBusiness4FlyoTraveller(payload);
      setInlineEditingField(null);
      setInlineEditValue('');
      setEditingTraveller(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error updating traveller: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [editingTraveller, inlineEditingField, inlineEditValue, agentId, ticketId, clientId, onUpdate]);

  // Close modal
  const handleCloseModal = () => {
    setShowTravellerModal(false);
    setEditingTraveller(null);
    setSelectedFlightTab(0);
  };

  // Save traveller
  const handleSaveTraveller = async () => {
    if (!agentId || !ticketId) {
      alert('Missing agentId or ticketId');
      return;
    }

    if (!clientId) {
      alert('Client ID is required');
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      alert('First name and last name are required');
      return;
    }

    try {
      setLoading(true);

      const apiFlightData = formData.flightData.map(fd => ({
        flightId: fd.flightId,
        seat: fd.seat || '',
        meal: fd.meal || '',
        baggage: {
          cabin: fd.baggageCabin || null,
          checkin: fd.baggageCheckin || null,
          additional: fd.baggageAdditional || null
        }
      }));

      const payload = {
        agentId: parseInt(agentId),
        clientId: parseInt(clientId),
        ticketId: parseInt(ticketId),
        firstName: formData.firstName,
        lastName: formData.lastName,
        countryCode: formData.countryCode,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth || '',
        passport: formData.passport,
        passportExpiry: formData.passportExpiry || '',
        nationality: formData.nationality,
        gender: formData.gender,
        flightData: apiFlightData
      };

      if (editingTraveller) {
        payload.travellerId = editingTraveller.travellerId;
        await updateBusiness4FlyoTraveller(payload);
      } else {
        await addBusiness4FlyoTraveller(payload);
      }

      handleCloseModal();
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error saving traveller: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!flights || flights.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-1">No Flight Information</h3>
        <p className="text-xs text-gray-500">No flight details are available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - No gradient */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Travellers</h2>
            <p className="text-xs text-gray-500">{travellers.length} passenger{travellers.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={handleAddTraveller}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
        >
          <Plus className="w-4 h-4" />
          Add Traveller
        </button>
      </div>

      {/* Travellers List */}
      {travellers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">No Travellers</h3>
          <p className="text-xs text-gray-500">Add passengers to this booking.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {travellers.map((traveller, index) => (
            <TravellerCard
              key={traveller.travellerId || index}
              traveller={traveller}
              index={index}
              flights={flights}
              loading={loading}
              inlineEditingField={inlineEditingField}
              editingTraveller={editingTraveller}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
              openDeleteModal={openDeleteModal}
              formatDate={formatDate}
              isExpanded={expandedTravellers.has(traveller.travellerId)}
              onToggleExpand={() => toggleTravellerExpand(traveller.travellerId)}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Traveller Modal */}
      <TravellerModal
        isOpen={showTravellerModal}
        onClose={handleCloseModal}
        editingTraveller={editingTraveller}
        flights={flights}
        formData={formData}
        setFormData={setFormData}
        selectedFlightTab={selectedFlightTab}
        setSelectedFlightTab={setSelectedFlightTab}
        loading={loading}
        handleSaveTraveller={handleSaveTraveller}
        formatSector={formatSector}
        initializeFlightData={initializeFlightData}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        traveller={travellerToDelete}
        loading={loading}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

// Traveller Card Component - defined outside main component to prevent re-creation
const TravellerCard = React.memo(({
  traveller,
  index,
  flights,
  loading,
  inlineEditingField,
  editingTraveller,
  inlineEditValue,
  setInlineEditValue,
  startInlineEdit,
  saveInlineEdit,
  cancelInlineEdit,
  openDeleteModal,
  formatDate,
  isExpanded,
  onToggleExpand
}) => {
  const isEditing = editingTraveller?.travellerId === traveller.travellerId;

  return (
    <div 
      className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      {/* Traveller Header - Clickable to expand/collapse */}
      <div 
        className="px-5 py-4 bg-blue-50 border-b border-gray-100 flex items-center justify-between cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">
              {traveller.firstname || 'N/A'} {traveller.lastname || 'N/A'}
            </h3>
            <p className="text-xs text-gray-500">Passenger {index + 1}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="p-2 rounded-xl bg-white text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-all shadow-sm"
            disabled={loading}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(traveller);
            }}
            className="p-2 rounded-xl bg-white text-red-500 hover:bg-red-50 hover:text-red-600 transition-all shadow-sm"
            disabled={loading}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Traveller Details - Only show when expanded */}
      {isExpanded && (
        <div className="p-5 space-y-5">
          {/* Basic Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <EditableInfoCard 
              icon={User} 
              label="First Name" 
              value={traveller.firstname} 
              field="firstname" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'firstname'}
              isEditable={true}
              color="blue"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
            <EditableInfoCard 
              icon={User} 
              label="Last Name" 
              value={traveller.lastname} 
              field="lastname" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'lastname'}
              isEditable={true}
              color="blue"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
            <EditableInfoCard 
              icon={UserCircle} 
              label="Gender" 
              value={traveller.gender === 'M' ? 'Male' : traveller.gender === 'F' ? 'Female' : traveller.gender} 
              field="gender" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'gender'}
              isEditable={true}
              color="blue"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
            <EditableInfoCard 
              icon={Phone} 
              label="Country Code" 
              value={traveller.countryCode} 
              field="countryCode" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'countryCode'}
              isEditable={true}
              color="blue"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
            <EditableInfoCard 
              icon={Phone} 
              label="Phone" 
              value={traveller.phone} 
              field="phone" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'phone'}
              isEditable={true}
              color="blue"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
            <EditableInfoCard 
              icon={Calendar} 
              label="Date of Birth" 
              value={formatDate(traveller.dateOfBirth)} 
              field="dateOfBirth" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'dateOfBirth'}
              isEditable={true}
              color="blue"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
          </div>

          {/* Passport Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <EditableInfoCard 
              icon={CreditCard} 
              label="Passport Number" 
              value={traveller.passport} 
              field="passport" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'passport'}
              isEditable={true}
              color="amber"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
            <EditableInfoCard 
              icon={Calendar} 
              label="Passport Expiry" 
              value={formatDate(traveller.passportExpiry)} 
              field="passportExpiry" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'passportExpiry'}
              isEditable={true}
              color="amber"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
            <EditableInfoCard 
              icon={Globe} 
              label="Nationality" 
              value={traveller.nationality} 
              field="nationality" 
              traveller={traveller} 
              isEditing={isEditing && inlineEditingField === 'nationality'}
              isEditable={true}
              color="blue"
              loading={loading}
              inlineEditValue={inlineEditValue}
              setInlineEditValue={setInlineEditValue}
              startInlineEdit={startInlineEdit}
              saveInlineEdit={saveInlineEdit}
              cancelInlineEdit={cancelInlineEdit}
            />
          </div>

          {/* Documents Section */}
          {traveller.documents && traveller.documents.length > 0 && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FileText className="w-3.5 h-3.5 text-blue-500" />
                </div>
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Documents ({traveller.documents.length})
                </span>
              </div>
              <div className="space-y-2">
                {traveller.documents.map((doc, docIndex) => (
                  <DocumentCard key={docIndex} doc={doc} index={docIndex} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Editable Info Card Component - defined outside main component
const EditableInfoCard = React.memo(({
  icon: Icon,
  label,
  value,
  field,
  traveller,
  isEditing,
  isEditable,
  color,
  loading,
  inlineEditValue,
  setInlineEditValue,
  startInlineEdit,
  saveInlineEdit,
  cancelInlineEdit
}) => {
  const [copied, setCopied] = useState(false);

  // Get the raw value for copying
  const getRawValue = useCallback(() => {
    if (field === 'phone') return traveller.phone || '';
    if (field === 'countryCode') return traveller.countryCode || '';
    if (field === 'passport') return traveller.passport || '';
    if (field === 'nationality') return traveller.nationality || '';
    if (field === 'gender') return traveller.gender || '';
    if (field === 'dateOfBirth') return traveller.dateOfBirth || '';
    if (field === 'passportExpiry') return traveller.passportExpiry || '';
    if (field === 'firstname') return traveller.firstname || '';
    if (field === 'lastname') return traveller.lastname || '';
    return value || '';
  }, [field, traveller, value]);

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    const textToCopy = getRawValue();
    if (textToCopy) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }, [getRawValue]);

  const handleStartEdit = useCallback(() => {
    startInlineEdit(traveller, field, 
      field === 'phone' ? traveller.phone :
      field === 'countryCode' ? traveller.countryCode :
      field === 'passport' ? traveller.passport :
      field === 'nationality' ? traveller.nationality :
      field === 'gender' ? traveller.gender :
      field === 'dateOfBirth' ? traveller.dateOfBirth :
      field === 'passportExpiry' ? traveller.passportExpiry :
      field === 'firstname' ? traveller.firstname :
      field === 'lastname' ? traveller.lastname : ''
    );
  }, [field, startInlineEdit, traveller]);

  const handleCardClick = useCallback(() => {
    if (isEditable && !isEditing) {
      handleStartEdit();
    }
  }, [isEditable, isEditing, handleStartEdit]);

  return (
    <div 
      onClick={handleCardClick}
      className={`group bg-white rounded-xl p-3 border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-300 ${isEditable && !isEditing ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      
      {isEditing ? (
        <div className="flex items-center gap-2">
          {field === 'gender' ? (
            <select
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              disabled={loading}
            >
              <option value="">Select</option>
              {genderOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : field === 'countryCode' ? (
            <select
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              disabled={loading}
            >
              {countryCodeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : field === 'nationality' ? (
            <select
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              className="flex-1 w-full min-w-0 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 truncate"
              disabled={loading}
              style={{ maxWidth: '100%' }}
            >
              <option value="">Select nationality...</option>
              <optgroup label="Popular">
                {nationalityOptions
                  .filter(opt => popularNationalities.includes(opt.value))
                  .map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
              </optgroup>
              <optgroup label="All Countries">
                {nationalityOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </optgroup>
            </select>
          ) : field === 'dateOfBirth' || field === 'passportExpiry' ? (
            <input
              type="date"
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              disabled={loading}
            />
          ) : (
            <input
              type="text"
              value={inlineEditValue}
              onChange={(e) => setInlineEditValue(e.target.value)}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              placeholder={`Enter ${label.toLowerCase()}`}
              disabled={loading}
            />
          )}
          <button 
            onClick={saveInlineEdit}
            disabled={loading}
            className="p-1.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button 
            onClick={cancelInlineEdit}
            disabled={loading}
            className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-800 truncate">
            {value || <span className="text-gray-400 italic">N/A</span>}
          </p>
          {isEditable && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button 
                onClick={handleCopy}
                className={`p-1.5 rounded-lg transition-colors ${copied ? 'bg-green-100 text-green-600' : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                disabled={loading}
                title="Copy"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartEdit();
                }}
                className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-colors"
                disabled={loading}
                title="Edit"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// Document Card Component - defined outside main component
const DocumentCard = React.memo(({ doc, index }) => (
  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-300">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">
            {doc.documentName || `Document ${index + 1}`}
          </p>
          {doc.documentUrl && (
            <p className="text-xs text-gray-500 truncate">
              {doc.documentUrl.length > 40 ? `${doc.documentUrl.substring(0, 40)}...` : doc.documentUrl}
            </p>
          )}
        </div>
      </div>
      {doc.documentUrl && (
        <div className="flex items-center gap-1 ml-2">
          <a
            href={doc.documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg bg-white text-blue-600 hover:bg-blue-50 transition-colors shadow-sm"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </a>
          <a
            href={doc.documentUrl}
            download
            className="p-2 rounded-lg bg-white text-blue-600 hover:bg-blue-50 transition-colors shadow-sm"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  </div>
));

// Traveller Modal Component - defined outside main component
const TravellerModal = React.memo(({
  isOpen,
  onClose,
  editingTraveller,
  flights,
  formData,
  setFormData,
  selectedFlightTab,
  setSelectedFlightTab,
  loading,
  handleSaveTraveller,
  formatSector
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 bg-blue-600 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold text-white">{editingTraveller ? 'Edit Traveller' : 'Add Traveller'}</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Traveller Details Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-500" />
                </div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Traveller Details</h3>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="First Name" required>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                    placeholder="Enter first name"
                  />
                </FormField>
                
                <FormField label="Last Name" required>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                    placeholder="Enter last name"
                  />
                </FormField>
                
                <FormField label="Gender">
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                  >
                    <option value="">Select gender</option>
                    {genderOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </FormField>
                
                <FormField label="Date of Birth">
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                  />
                </FormField>
                
                <FormField label="Country Code">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                  >
                    {countryCodeOptions.map(code => (
                      <option key={code.value} value={code.value}>{code.label}</option>
                    ))}
                  </select>
                </FormField>
                
                <FormField label="Phone">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                    placeholder="Enter phone number"
                  />
                </FormField>
                
                <FormField label="Passport">
                  <input
                    type="text"
                    value={formData.passport}
                    onChange={(e) => setFormData(prev => ({ ...prev, passport: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                    placeholder="Enter passport number"
                  />
                </FormField>
                
                <FormField label="Passport Expiry">
                  <input
                    type="date"
                    value={formData.passportExpiry}
                    onChange={(e) => setFormData(prev => ({ ...prev, passportExpiry: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                  />
                </FormField>
                
                <FormField label="Nationality">
                  <select
                    value={formData.nationality}
                    onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))}
                    className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                  >
                    <option value="">Select nationality</option>
                    {nationalityOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </FormField>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-gray-200"></div>

            {/* Flight-Specific Details Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Plane className="w-4 h-4 text-blue-500" />
                </div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Flight-Specific Details</h3>
              </div>

              {/* Flight Tabs */}
              {flights.length > 1 && (
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {flights.map((flight, index) => (
                    <button
                      key={flight.flightId || index}
                      onClick={() => setSelectedFlightTab(index)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                        selectedFlightTab === index
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <Plane className="w-4 h-4" />
                      Flight {index + 1}: {formatSector(flight.departure, flight.arrival)}
                    </button>
                  ))}
                </div>
              )}

              {/* Selected Flight Form */}
              {flights.map((flight, flightIndex) => (
                selectedFlightTab === flightIndex && (
                  <div key={flight.flightId || flightIndex} className="space-y-4">
                    {/* Flight Info */}
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                        <Plane className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{flight.flightNumber || 'N/A'}</p>
                        <p className="text-xs text-gray-500">
                          {flight.departure?.city || flight.departure?.iata || 'N/A'} ({flight.departure?.iata || 'N/A'})
                          <ChevronRight className="w-3 h-3 inline mx-1" />
                          {flight.arrival?.city || flight.arrival?.iata || 'N/A'} ({flight.arrival?.iata || 'N/A'})
                        </p>
                      </div>
                    </div>

                    {/* Service Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField label="Seat">
                        <input
                          type="text"
                          value={formData.flightData[flightIndex]?.seat || ''}
                          onChange={(e) => {
                            const newFlightData = [...formData.flightData];
                            newFlightData[flightIndex] = {
                              ...newFlightData[flightIndex],
                              seat: e.target.value
                            };
                            setFormData(prev => ({ ...prev, flightData: newFlightData }));
                          }}
                          className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                          placeholder="e.g., 12A"
                        />
                      </FormField>
                      
                      <FormField label="Meal">
                        <input
                          type="text"
                          value={formData.flightData[flightIndex]?.meal || ''}
                          onChange={(e) => {
                            const newFlightData = [...formData.flightData];
                            newFlightData[flightIndex] = {
                              ...newFlightData[flightIndex],
                              meal: e.target.value
                            };
                            setFormData(prev => ({ ...prev, flightData: newFlightData }));
                          }}
                          className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                          placeholder="e.g., VEG"
                        />
                      </FormField>
                      
                      <FormField label="Cabin Baggage">
                        <input
                          type="text"
                          value={formData.flightData[flightIndex]?.baggageCabin || ''}
                          onChange={(e) => {
                            const newFlightData = [...formData.flightData];
                            newFlightData[flightIndex] = {
                              ...newFlightData[flightIndex],
                              baggageCabin: e.target.value
                            };
                            setFormData(prev => ({ ...prev, flightData: newFlightData }));
                          }}
                          className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                          placeholder="e.g., 7kg"
                        />
                      </FormField>
                      
                      <FormField label="Check-in Baggage">
                        <input
                          type="text"
                          value={formData.flightData[flightIndex]?.baggageCheckin || ''}
                          onChange={(e) => {
                            const newFlightData = [...formData.flightData];
                            newFlightData[flightIndex] = {
                              ...newFlightData[flightIndex],
                              baggageCheckin: e.target.value
                            };
                            setFormData(prev => ({ ...prev, flightData: newFlightData }));
                          }}
                          className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                          placeholder="e.g., 20kg"
                        />
                      </FormField>
                      
                      <FormField label="Additional Baggage">
                        <input
                          type="text"
                          value={formData.flightData[flightIndex]?.baggageAdditional || ''}
                          onChange={(e) => {
                            const newFlightData = [...formData.flightData];
                            newFlightData[flightIndex] = {
                              ...newFlightData[flightIndex],
                              baggageAdditional: e.target.value
                            };
                            setFormData(prev => ({ ...prev, flightData: newFlightData }));
                          }}
                          className="w-full text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 bg-white"
                          placeholder="e.g., 10kg"
                        />
                      </FormField>
                    </div>
                  </div>
                )
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTraveller}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    {editingTraveller ? 'Update Traveller' : 'Add Traveller'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Delete Modal Component - defined outside main component
const DeleteModal = React.memo(({ show, traveller, loading, onClose, onConfirm }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-red-50 border-b border-red-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Delete Passenger</h2>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <span className="font-semibold text-gray-900">{traveller?.firstname} {traveller?.lastname}</span>? This action cannot be undone.
          </p>
        </div>
        
        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

// Form Field Component - defined outside main component
const FormField = React.memo(({ label, children, required = false }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
));

export default B4FTravellerTab;
